import { ComplaintFlowProps, ComplaintFlowInputs } from 'features/complaint/views/flow/types'
import { FormProvider, useFormContext, useWatch } from 'react-hook-form'
import { Button, Form, formatFileSize, Heading, TextArea, TextField } from '@boltenergy-be/design-system'
import { useTranslation } from 'next-i18next/pages'
import FileUpload from 'components/inputs/file-upload/FileUpload'
import {
  COMPLAINT_FILE_UPLOAD_AMOUNT_LIMIT,
  COMPLAINT_FILE_UPLOAD_SIZE_LIMIT,
  MAX_COMPLAINT_CHARACTERS
} from 'features/complaint/views/flow/constants'
import { useGetLanguage } from 'hooks/useGetLanguage'

const Complaint = ({ setCurrentStep }: ComplaintFlowProps) => {
  // i18n
  const { t } = useTranslation('complaint', { keyPrefix: 'flow.steps.complaint' })
  const { t: tX } = useTranslation(['common', 'validation'])
  const language = useGetLanguage('uppercase')

  // React Hook Form
  const hookForm = useFormContext<Pick<ComplaintFlowInputs, 'complaint'>>()
  const { handleSubmit, register, formState, control } = hookForm
  const watchComplaint = useWatch({ control, name: 'complaint.description' })

  /**
   * Submit handler for the contact step of the complaint flow.
   */
  const submit = () => {
    setCurrentStep('overview')
  }

  return (
    <section>
      <header>
        <Heading as="h2">{t('title')}</Heading>
        <p>{t('description')}</p>
      </header>

      <Form onSubmit={handleSubmit(submit)}>
        <TextField
          {...register('complaint.subject', {
            required: tX('validation:required'),
            maxLength: {
              value: 100,
              message: tX('validation:max.string', {
                attribute: t('subject.label'),
                length: 100
              })
            }
          })}
          grid="full"
          label={t('subject.label')}
          description={t('subject.description')}
          required
          error={formState.errors?.complaint?.subject?.message}
        />

        <TextArea
          {...register('complaint.description', {
            required: tX('validation:required'),
            maxLength: {
              value: MAX_COMPLAINT_CHARACTERS,
              message: t('validation:max.string', {
                attribute: t('reason.label'),
                length: MAX_COMPLAINT_CHARACTERS
              })
            }
          })}
          grid="full"
          label={t('reason.label')}
          description={t('reason.description')}
          required
          maxLength={MAX_COMPLAINT_CHARACTERS}
          error={formState.errors?.complaint?.description?.message}
          message={
            watchComplaint?.length === MAX_COMPLAINT_CHARACTERS ? { type: 'informative', text: t('reason.limitReached') } : undefined
          }
        />

        <FormProvider {...hookForm}>
          <FileUpload
            identifier="attachments"
            parentDotPath="complaint"
            multiple
            maxAmountFiles={COMPLAINT_FILE_UPLOAD_AMOUNT_LIMIT}
            limit={COMPLAINT_FILE_UPLOAD_SIZE_LIMIT}
            label={t('attachment.label')}
            description={t('attachment.description', {
              amountLimit: 15,
              sizeLimit: formatFileSize(COMPLAINT_FILE_UPLOAD_SIZE_LIMIT, language)
            })}
          />
        </FormProvider>

        <Form.Footer className="flex-row mt-400">
          <Button type="button" leadingIcon="arrowLeft" variant="secondary" onClick={() => setCurrentStep('contact')}>
            {tX('previous')}
          </Button>

          <Button type="submit" trailingIcon="arrowRight" className="ml-auto">
            {tX('next')}
          </Button>
        </Form.Footer>
      </Form>
    </section>
  )
}

export default Complaint
